import { useEffect } from "react";
import { GraduationCap } from "lucide-react";
import { Label } from "../ui/label";
import { TimeSelect, followMeridiem } from "./TimeSelect";
import { cn } from "../../lib/utils";
import { formatTime, timeToMinutes } from "../../lib/dateUtils";
import { parseTimeParts } from "../../lib/timeOfDay";

export interface LessonValue {
  firstHour: boolean;
  startTime: string;
  endTime: string;
}

interface LessonSectionProps {
  /** Current event start time — used to auto-fill when firstHour is on */
  eventStartTime: string;
  /** null = lesson not included */
  value: LessonValue | null;
  onChange: (value: LessonValue | null) => void;
}

function addOneHour(time: string): string {
  // No start time yet means no hour after it — better empty than "NaN:00".
  if (!parseTimeParts(time)) return "";
  const minutes = (timeToMinutes(time) + 60) % 1440;
  const hour = Math.floor(minutes / 60);
  return `${String(hour).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

export function LessonSection({ eventStartTime, value, onChange }: LessonSectionProps) {
  const included = value !== null;

  // When "1st hour" is on, keep lesson times in sync with the event start time.
  useEffect(() => {
    if (!value?.firstHour || !eventStartTime) return;
    const newEnd = addOneHour(eventStartTime);
    if (value.startTime !== eventStartTime || value.endTime !== newEnd) {
      onChange({ ...value, startTime: eventStartTime, endTime: newEnd });
    }
    // intentionally omit `value` and `onChange` to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventStartTime, value?.firstHour]);

  const toggle = () => {
    if (included) {
      onChange(null);
    } else {
      // Default to 1st hour so times are filled in immediately
      const end = eventStartTime ? addOneHour(eventStartTime) : "";
      onChange({ firstHour: true, startTime: eventStartTime ?? "", endTime: end });
    }
  };

  const update = (patch: Partial<LessonValue>) => {
    if (!value) return;
    const next = { ...value, ...patch };
    // If firstHour just turned on, snap times to event start
    if (patch.firstHour && eventStartTime) {
      next.startTime = eventStartTime;
      next.endTime = addOneHour(eventStartTime);
    }
    onChange(next);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      {/* Toggle row */}
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2.5">
          <GraduationCap className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Lesson included?</span>
        </div>
        <div className={cn(
          "relative w-10 h-6 rounded-full transition-colors duration-200",
          included ? "bg-primary" : "bg-secondary border border-border"
        )}>
          <div className={cn(
            "absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200",
            included ? "translate-x-5" : "translate-x-1"
          )} />
        </div>
      </button>

      {included && value && (
        <div className="space-y-3 pt-1">
          {/* 1st hour shortcut */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={value.firstHour}
              onChange={(e) => update({ firstHour: e.target.checked })}
              className="accent-primary"
            />
            <span className="text-sm text-foreground">1st hour of event</span>
            {value.firstHour && addOneHour(eventStartTime) && (
              <span className="text-xs text-muted-foreground">
                ({formatTime(eventStartTime)} – {formatTime(addOneHour(eventStartTime))})
              </span>
            )}
          </label>

          {/* Lesson time fields — shown always so user can adjust if not 1st hour */}
          {/* One per row on a phone — an hour, a minute and AM/PM do not fit
              in half a narrow screen. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Lesson start</Label>
              <TimeSelect
                value={value.startTime}
                onChange={(t) => update({ startTime: t, firstHour: false })}
                placeholder="Start"
                defaultMeridiem={followMeridiem(eventStartTime)}
                aria-label="Lesson start hour"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Lesson end</Label>
              <TimeSelect
                value={value.endTime}
                onChange={(t) => update({ endTime: t, firstHour: false })}
                placeholder="End"
                defaultMeridiem={followMeridiem(value.startTime || eventStartTime)}
                aria-label="Lesson end hour"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
